/* eslint-disable import/prefer-default-export,max-len,no-nested-ternary */
// eslint-disable-next-line import/no-cycle
import { createLine, getLength } from '../line';
import { IObject, IPoint } from '../../types/ark1.0/types';
import { round } from '../round';

export const getX = (pos: IObject): number => pos.x || pos.X || 0;

export const getY = (pos: IObject): number => pos.y || pos.Y || 0;

export const stringify = (({ x, y } : IPoint): string => JSON.stringify({ x, y }));
export const compare = (a: IPoint, b: IPoint): number => {
  if (a === b) {
    return 0;
  }
  if (a.y > b.y) {
    return 1;
  }
  if (a.y < b.y) {
    return -1;
  }
  if (a.x > b.x) {
    return 1;
  }
  if (a.x < b.x) {
    return -1;
  }
  return 0;
};

export const sort = (points: IPoint[], comparator: (a: IPoint, b: IPoint)=> number = compare) => points.sort(comparator);
export const equalPoint = (point1: IPoint, point2: IPoint): boolean => point1.x === point2.x && point1.y === point2.y;

export const distinct = (points: IPoint[]): IPoint[] => {
  const has = (list: IPoint[], point: IPoint): boolean => !!list.find((p) => equalPoint(p, point));
  const put = (list: IPoint[], ...point: IPoint[]):IPoint[] => {
    list.push(...point.filter((p) => !has(list, p)));
    return list;
  };
  return points.reduce(
    (acc: IPoint[], point) => put(acc, point),
    [],
  );
};

export const roundPoint = (point: IPoint): IPoint => ({
  ...point,
  x: round(point.x),
  y: round(point.y),
});

export const getHorizontalDelta = (point1: IPoint, point2: IPoint, divider = 1): IPoint => {
  const deltaX = (point1.x - point2.x) / divider;
  return roundPoint({
    x: deltaX,
    y: 0,
  });
};

// getXDelta
export const getVerticalDelta = (point1: IPoint, point2: IPoint, divider = 1): IPoint => {
  const deltaY = (point1.y - point2.y) / divider;
  return roundPoint({
    x: 0,
    y: deltaY,
  });
};

export const getDelta = (point1: IPoint, point2: IPoint, divider = 1): IPoint => roundPoint({
  x: (point1.x - point2.x) / divider,
  y: (point1.y - point2.y) / divider,
});
/**
 *  if pressed shift allow vertical movements only
 *  if pressed ctrlKey allow horizontal movements only
 */
export function getDeltaFn({ shiftKey, ctrlKey }: { shiftKey: boolean, ctrlKey: boolean }) {
  return shiftKey
    ? getVerticalDelta
    : ctrlKey
      ? getHorizontalDelta
      : getDelta;
}

export const getDistance = (point1: IPoint, point2: IPoint): number => {
  const delta = getDelta(point1, point2);
  return Math.sqrt(delta.x * delta.x + delta.y * delta.y);
};

export const move = (point: IPoint, delta: IPoint): IPoint => roundPoint({
  x: point.x - delta.x,
  y: point.y - delta.y,
});

export const getDeltaSign = (p0: IPoint, p1: IPoint): IPoint => ({
  x: p0.x < p1.x
    ? -1
    : p0.x === p1.x
      ? 0
      : 1,
  y: p0.y < p1.y
    ? -1
    : p0.y === p1.y
      ? 0
      : 1,
});

export const getNearestPoint = (point: IPoint, points: IPoint[], skipFirstPoint?: boolean) => (skipFirstPoint
  ? points.filter((p) => !equalPoint(p, point))
  : points
).reduce<{ value: number, point: IPoint | undefined }>(
  (min, p) => {
    const length = getLength(createLine(point, p));
    return length > min.value ? min : { value: length, point: p };
  },
  {
    value: Number.POSITIVE_INFINITY,
    point: undefined,
  },
);
